/**
 * VM Snapshots Management Page
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CameraIcon
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import { vmApi } from '../services/vmApi';
import { VMSnapshot, VMSnapshotStatus } from '../types/vm';
import { Project } from '../types';
import CreateSnapshotModal from '../components/vms/CreateSnapshotModal';
import RestoreSnapshotModal from '../components/vms/RestoreSnapshotModal';
import { useSelectedProject } from '../hooks/useSelectedProject';

const VMSnapshots: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const queryClient = useQueryClient();

  const {
    selectedProjectId: globalProjectId,
    projects,
    setSelectedProject: setGlobalProject,
    autoSelectFirstProject,
  } = useSelectedProject();

  // Use URL param if available, otherwise use global selection
  const selectedProject = projectFromUrl || globalProjectId;

  const [selectedVM, setSelectedVM] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<VMSnapshot | null>(null);
  const [filters, setFilters] = useState<{ status?: string; vm_id?: string }>({});

  const projectId = selectedProject || undefined;

  // Auto-select first project if none selected and no URL param
  useEffect(() => {
    if (!projectFromUrl) {
      autoSelectFirstProject();
    }
  }, [projectFromUrl, autoSelectFirstProject]);

  // Fetch snapshots
  const { data: snapshotsData, isLoading, isError, refetch } = useQuery({
    queryKey: ['snapshots', projectId, filters],
    queryFn: () => vmApi.listSnapshots({
      project_id: projectId,
      status: filters.status as any,
      vm_id: filters.vm_id
    }),
    enabled: !!projectId,
  });

  const snapshots: VMSnapshot[] = snapshotsData?.snapshots || [];

  // Calculate stats
  const stats = useMemo(() => {
    const ready = snapshots.filter(s => s.status === 'ready').length;
    const creating = snapshots.filter(s => s.status === 'creating').length;
    const error = snapshots.filter(s => s.status === 'error').length;
    const total = snapshots.length;
    return { ready, creating, error, total };
  }, [snapshots]);

  // Delete snapshot mutation
  const deleteMutation = useMutation({
    mutationFn: (snapshotId: string) => vmApi.deleteSnapshot(snapshotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['snapshots'] });
      toast.success('Snapshot deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete snapshot');
    },
  });

  // Restore snapshot handler
  const handleRestore = (snapshot: VMSnapshot) => {
    setSelectedSnapshot(snapshot);
    setShowRestoreModal(true);
  };

  // Delete snapshot handler
  const handleDelete = (snapshot: VMSnapshot) => {
    if (window.confirm(`Are you sure you want to delete snapshot "${snapshot.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(snapshot.id);
    }
  };

  const getStatusBadge = (status: VMSnapshotStatus) => {
    const statusConfig = {
      creating: { color: 'bg-yellow-900/50 text-yellow-400', icon: ClockIcon, label: 'Creating' },
      ready: { color: 'bg-green-900/50 text-green-400', icon: CheckCircleIcon, label: 'Ready' },
      error: { color: 'bg-red-900/50 text-red-400', icon: XCircleIcon, label: 'Error' },
      deleting: { color: 'bg-gray-700 text-gray-300', icon: TrashIcon, label: 'Deleting' },
    };

    const config = statusConfig[status] || statusConfig.error;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      <Header
        title="VM Snapshots"
        subtitle="Manage and restore VM snapshots"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Create Snapshot
          </button>
        }
      />

      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Ready</p>
                <p className="mt-2 text-3xl font-bold text-green-500">{stats.ready}</p>
              </div>
              <div className="bg-green-900/50 p-3 rounded-xl">
                <CheckCircleIcon className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Creating</p>
                <p className="mt-2 text-3xl font-bold text-yellow-500">{stats.creating}</p>
              </div>
              <div className="bg-yellow-900/50 p-3 rounded-xl">
                <ClockIcon className="w-6 h-6 text-yellow-500" />
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Error</p>
                <p className="mt-2 text-3xl font-bold text-red-500">{stats.error}</p>
              </div>
              <div className="bg-red-900/50 p-3 rounded-xl">
                <XCircleIcon className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Total</p>
                <p className="mt-2 text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="bg-primary-900/50 p-3 rounded-xl">
                <CameraIcon className="w-6 h-6 text-primary-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">Project:</label>
              <select
                value={selectedProject}
                onChange={(e) => {
                  const newProject = e.target.value;
                  setGlobalProject(newProject);
                  // Update URL
                  const newParams = new URLSearchParams(searchParams);
                  if (newProject) {
                    newParams.set('project', newProject);
                  } else {
                    newParams.delete('project');
                  }
                  setSearchParams(newParams);
                }}
                className="px-3 py-2 border border-gray-700 rounded-md text-sm bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[200px] transition-colors"
                aria-label="Filter by project"
              >
                <option value="">All Projects</option>
                {projects.map((project: Project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">Status:</label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
                className="px-3 py-2 border border-gray-700 rounded-md text-sm bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                aria-label="Filter by status"
              >
                <option value="">All Statuses</option>
                <option value="creating">Creating</option>
                <option value="ready">Ready</option>
                <option value="error">Error</option>
                <option value="deleting">Deleting</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">VM ID:</label>
              <input
                type="text"
                placeholder="Filter by VM ID..."
                value={filters.vm_id || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, vm_id: e.target.value || undefined }))}
                className="px-3 py-2 border border-gray-700 rounded-md text-sm bg-gray-800 text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[200px] transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Snapshots List */}
        {!projectId ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-700 mb-4">
                <CameraIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Select a Project</h3>
              <p className="mt-2 text-sm text-gray-400 max-w-md mx-auto">
                Please select a project from the filter above to view snapshots.
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-gray-400">Loading snapshots...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <p className="text-red-500 mb-4">Failed to load snapshots</p>
              <button
                onClick={() => refetch()}
                className="btn-primary"
              >
                Retry
              </button>
            </div>
          </div>
        ) : snapshots.length === 0 ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-700 mb-4">
                <CameraIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">No Snapshots</h3>
              <p className="mt-2 text-sm text-gray-400 max-w-md mx-auto">
                Get started by creating a new snapshot for your virtual machines.
              </p>
              <div className="mt-8">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors shadow-sm"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create Snapshot
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Snapshot Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      VM ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-900 divide-y divide-gray-700">
                  {snapshots.map((snapshot) => (
                    <tr key={snapshot.id} className="hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{snapshot.name}</div>
                        {snapshot.description && (
                          <div className="text-sm text-gray-400">{snapshot.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white font-mono">{snapshot.vm_id.substring(0, 8)}...</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(snapshot.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatSize(snapshot.size_bytes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatDate(snapshot.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {snapshot.is_automatic ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-primary-900/50 text-primary-400">
                            <ClockIcon className="w-3 h-3" />
                            Automatic
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Manual</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {snapshot.status === 'ready' && (
                            <button
                              onClick={() => handleRestore(snapshot)}
                              className="text-primary-500 hover:text-primary-400 transition-colors"
                              title="Restore snapshot"
                            >
                              <ArrowPathIcon className="w-5 h-5" />
                            </button>
                          )}
                          {snapshot.status !== 'deleting' && (
                            <button
                              onClick={() => handleDelete(snapshot)}
                              disabled={deleteMutation.isPending}
                              className="text-red-500 hover:text-red-400 disabled:opacity-50 transition-colors"
                              title="Delete snapshot"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create Snapshot Modal */}
        {showCreateModal && (
          <CreateSnapshotModal
            isOpen={showCreateModal}
            onClose={() => {
              setShowCreateModal(false);
              setSelectedVM(null);
            }}
            onSuccess={() => {
              setShowCreateModal(false);
              setSelectedVM(null);
              queryClient.invalidateQueries({ queryKey: ['snapshots'] });
            }}
            initialVMId={selectedVM || undefined}
          />
        )}

        {/* Restore Snapshot Modal */}
        {showRestoreModal && selectedSnapshot && (
          <RestoreSnapshotModal
            isOpen={showRestoreModal}
            onClose={() => {
              setShowRestoreModal(false);
              setSelectedSnapshot(null);
            }}
            onSuccess={() => {
              setShowRestoreModal(false);
              setSelectedSnapshot(null);
              queryClient.invalidateQueries({ queryKey: ['snapshots'] });
              queryClient.invalidateQueries({ queryKey: ['vms'] });
            }}
            snapshot={selectedSnapshot}
          />
        )}
      </div>
    </div>
  );
};

export default VMSnapshots;
